import { Printer } from "@redotech/redo-fulfillment-group-common/printer";
import { useStreamingEnabled } from "@redotech/redo-merchant-app-common/streaming-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { bijectionOptional } from "@redotech/util/bijection";
import { storeTransform } from "@redotech/util/store";
import { numberStringFormat } from "@redotech/util/string-format";
import { localStore } from "@redotech/web-util/storage";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { CloseStream, useWatchStream } from "../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export interface PrinterContextValue {
  computers: Printer["computer"][];
  printers: Printer[];
  loading: boolean;
  hasMultipleConnections: boolean;
  multipleConnectionComputerIds: number[];
  printerIdIndex: Record<number, number>;
}

export const PrinterContext = createContext<PrinterContextValue>({
  computers: [],
  printers: [],
  loading: true,
  hasMultipleConnections: false,
  multipleConnectionComputerIds: [],
  printerIdIndex: {},
});

export const redoConnectComputerIdStore = storeTransform(
  localStore("redo.selectedRedoConnectComputer"),
  bijectionOptional(numberStringFormat),
);

export const PrinterProvider = memo(function PrintersProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [computers, setComputers] = useState<Printer["computer"][]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [hasMultipleConnections, setHasMultipleConnections] = useState(false);
  const [multipleConnectionComputerIds, setMultipleConnectionComputerIds] =
    useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [printerIdIndex, setPrinterIdIndex] = useState<Record<number, number>>(
    {},
  );

  const client = useContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const isOmsEnabled = team?.settings.outboundLabels?.newOmsEnabled;
  const streamingEnabled = useStreamingEnabled();

  useEffect(() => {
    if (!client || !isOmsEnabled || !streamingEnabled) {
      return;
    }

    void client.refreshPrintNodeIntegration({});
    const interval = setInterval(
      () => {
        void client.refreshPrintNodeIntegration({});
      },
      Temporal.Duration.from({ minutes: 10 }).total("millisecond"),
    );

    return () => clearInterval(interval);
  }, [client, isOmsEnabled, streamingEnabled]);

  useWatchStream(
    isOmsEnabled && streamingEnabled ? client : undefined,
    "watchPrinters",
    {},
    ({
      computers,
      printers,
      hasMultipleConnections,
      multipleConnectionComputerIds,
      notEnabled,
    }) => {
      if (notEnabled) {
        throw new CloseStream();
      }

      const sortedPrinters = printers.sort(
        (a, b) => Number(a.state === "online") - Number(b.state === "online"),
      );

      setHasMultipleConnections(hasMultipleConnections);
      setMultipleConnectionComputerIds(multipleConnectionComputerIds);
      setComputers(computers);
      setPrinters(sortedPrinters);
      setLoading(false);
      setPrinterIdIndex(
        sortedPrinters.reduce(
          (acc, printer, index) => ({ ...acc, [printer.id]: index }),
          {} as Record<number, number>,
        ),
      );
    },
    true,
    { disableCompression: true },
  );

  return (
    <PrinterContext.Provider
      value={{
        computers,
        printers,
        loading,
        printerIdIndex,
        hasMultipleConnections,
        multipleConnectionComputerIds,
      }}
    >
      {children}
    </PrinterContext.Provider>
  );
});
